import type { EntityListHoldersResponse } from '@ydistri/api-sdk';
import { ReadOnlyItem } from '@ydistri/ds';
import { computeRemSize } from '@ydistri/theme';
import { Space, Spin, Typography } from 'antd';
import React from 'react';
import { styled } from 'styled-components';

const HolderList = styled.ul`
  margin: 0;
  padding-left: ${computeRemSize(20)};
`;

interface EntityListHoldersProps {
  holders?: EntityListHoldersResponse;
  isDefault: boolean;
  isError: boolean;
  isFetching: boolean;
  labelWidth: string;
}

/** What holds the list besides calculations; a held list cannot be deleted. */
const EntityListHolders: React.FC<EntityListHoldersProps> = ({
  holders,
  isDefault,
  isError,
  isFetching,
  labelWidth,
}) => {
  const items = [
    ...(isDefault ? [{ key: 'default', text: 'Default list' }] : []),
    ...(holders?.scopes ?? []).map(scope => ({
      key: `scope-${scope.scopeId}`,
      text: `Scope “${scope.name}”`,
    })),
    ...(holders?.asoScenarios ?? []).map(({ request, scenario }) => ({
      key: `aso-${scenario.id}`,
      text: `ASO request #${request.id} “${request.name}” → scenario #${scenario.id} “${scenario.name}”`,
    })),
    ...(holders?.shortTermForecasts ?? []).map(forecast => ({
      key: `forecast-${forecast.shortTermForecastId}`,
      text: `Short-term forecast “${forecast.name}”`,
    })),
    ...(holders?.isHeldByDeletedCalculation
      ? [{ key: 'deleted-calculation', text: 'Held by a deleted calculation until it is removed.' }]
      : []),
  ];
  const hasItems = items.length > 0;

  return (
    <ReadOnlyItem alignItems="flex-start" label="Also used by" labelWidth={labelWidth}>
      <Space direction="vertical" size="small">
        {hasItems && (
          <HolderList aria-label="Entity list holders">
            {items.map(item => (
              <li key={item.key}>{item.text}</li>
            ))}
          </HolderList>
        )}
        {isFetching && (
          <Space size="small">
            <Spin size="small" />
            <Typography.Text type="secondary">
              {holders ? 'Refreshing usages…' : 'Loading usages…'}
            </Typography.Text>
          </Space>
        )}
        {!hasItems && !isFetching && !isError && <Typography.Text>Not used</Typography.Text>}
        {isError && (
          <Typography.Text type="warning">
            {holders ? 'Usages could not be refreshed.' : 'Usages could not be loaded.'}
          </Typography.Text>
        )}
      </Space>
    </ReadOnlyItem>
  );
};

export default EntityListHolders;
